import React, { useState } from 'react'
import styled from 'styled-components'
import ParseContent from 'components/shared/ParseContent'
import chevronAccUrl from 'img/icon/chevron-acc.svg'

const StyledFaq = styled.section`
  position: relative;
  z-index: 1;
  padding-top: 30px;
  padding-bottom: 30px;

  @media screen and (max-width: 991px) {
    padding-top: 20px;
    padding-bottom: 20px;
  }
`

const StyledContainer = styled.div`
  height: 100%;

  @media screen and (max-width: 991px) {
    height: unset;
    position: relative;
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 30px;

  @media screen and (max-width: 991px) {
    flex-direction: column;
    gap: 20px;
  }
`

const HeaderBlock = styled.div`
  width: 50%;
  flex-shrink: 0;

  & h2 {
    font-weight: ${(props) => props.theme.font.weight.xl};

    @media screen and (min-width: 992px) {
      font-size: ${(props) => props.theme.font.size.xl};
    }

    @media screen and (max-width: 991px) {
      font-size: ${(props) => props.theme.font.size.l};
    }
  }

  @media screen and (max-width: 991px) {
    width: 100%;
  }
`

const QuestionsBlock = styled.div`
  width: 50%;
  flex: 1;

  @media screen and (max-width: 991px) {
    width: 100%;
  }
`

const QuestionItem = styled.div`
  border-bottom: 1px solid ${(props) => props.theme.color.face.lightBorder};

  &:first-of-type {
    border-top: 1px solid ${(props) => props.theme.color.face.lightBorder};
  }
`

const QuestionTrigger = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 0;
  background: none;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  color: ${(props) => props.theme.color.text.main};
  font-size: ${(props) => props.theme.font.size.m};
  pointer-events: auto;
  position: relative;
  z-index: 1;

  & h3 {
    margin: 0;
    font-size: ${(props) => props.theme.font.size.mx};
    font-weight: 700;
    flex: 1;
    color: inherit;
  }
`

const Chevron = styled.span`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  background: ${(props) => props.theme.color.gradient.main};
  -webkit-mask: url(${chevronAccUrl}) no-repeat center;
  mask: url(${chevronAccUrl}) no-repeat center;
  -webkit-mask-size: contain;
  mask-size: contain;
  transform: rotate(${(props) => (props.$isOpen ? '90deg' : '-90deg')});
  transition: transform 0.2s ease;
`

const AnswerWrapper = styled.div`
  display: grid;
  height: ${(props) => (props.$isOpen ? 'auto' : '0')};
  transition: height 0.25s ease;
  overflow: hidden;
  min-height: 0;
`

const AnswerInner = styled.div`
  overflow: hidden;
  min-height: 0;

  & > div {
    padding-bottom: 20px;
  }

  & p:last-child {
    margin-bottom: 0;
  }
`

const Faq = ({ className = '', content, id = 'faq' }) => {
  const [openIndex, setOpenIndex] = useState(null)
  if (!content) return null

  const handleToggle = (e, index) => {
    e.preventDefault()
    e.stopPropagation()
    setOpenIndex((prev) => (prev === index ? null : index))
  }

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleToggle(e, index)
    }
  }

  return (
    <StyledFaq id={id} className={`${className}`}>
      <StyledContainer className="container">
        <Wrapper>
          <HeaderBlock>
            <h2>{content?.title}</h2>
          </HeaderBlock>
          <QuestionsBlock>
            {content?.items?.map((item, index) => {
              const isOpen = openIndex === index
              const questionText = item?.questions ?? item?.question ?? ''
              return (
                <QuestionItem key={index}>
                  <QuestionTrigger
                    role="button"
                    tabIndex={0}
                    aria-expanded={isOpen}
                    aria-controls={`faq-answer-${index}`}
                    id={`faq-question-${index}`}
                    onClick={(e) => handleToggle(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                  >
                    <h3>{questionText}</h3>
                    <Chevron $isOpen={isOpen} aria-hidden />
                  </QuestionTrigger>
                  <AnswerWrapper
                    id={`faq-answer-${index}`}
                    role="region"
                    aria-labelledby={`faq-question-${index}`}
                    $isOpen={isOpen}
                  >
                    <AnswerInner>
                      <div>
                        <ParseContent content={item.answer} />
                      </div>
                    </AnswerInner>
                  </AnswerWrapper>
                </QuestionItem>
              )
            })}
          </QuestionsBlock>
        </Wrapper>
      </StyledContainer>
    </StyledFaq>
  )
}

export default Faq
